import { X, PlayCircle, Search } from "lucide-react";
import { youtubeSearchUrl } from "@/lib/videos";
import { useEffect } from "react";

export default function VideoDialog({ exercise, onClose }) {
  useEffect(() => {
    if (!exercise) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [exercise, onClose]);

  if (!exercise) return null;

  const videoId = exercise.youtube_id || "";
  const embedUrl = videoId
    ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`
    : "";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-dialog"
    >
      <div
        className="w-full max-w-4xl bg-[#0B0B0F] border border-[#2A2A35] shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#2A2A35]">
          <div className="flex items-center gap-2 min-w-0">
            <PlayCircle className="w-4 h-4 text-[#E2FF31] flex-shrink-0" strokeWidth={2.5} />
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB]">Video demo</div>
              <div className="font-display text-lg font-bold uppercase leading-tight truncate">
                {exercise.name}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="video-dialog-close"
            className="border border-[#2A2A35] hover:border-[#FF3B30] hover:text-[#FF3B30] p-2 transition-all flex-shrink-0"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Video */}
        <div className="relative w-full bg-black" style={{ aspectRatio: "16/9" }}>
          {videoId ? (
            <iframe
              key={videoId}
              src={embedUrl}
              title={`Video demo: ${exercise.name}`}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="video-iframe"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#A0A0AB]">
              Nessun video disponibile
            </div>
          )}
        </div>

        {/* Tips + fallback */}
        <div className="p-5 space-y-3">
          {exercise.tips && (
            <div className="text-xs text-white/80 border-l-2 border-[#E2FF31] pl-3">
              <span className="text-[9px] uppercase tracking-widest text-[#E2FF31]">Tip · </span>
              {exercise.tips}
            </div>
          )}
          <a
            href={youtubeSearchUrl(exercise)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-dialog-search-more"
            className="w-full border border-[#2A2A35] hover:border-white text-white text-xs font-bold uppercase tracking-widest py-3 flex items-center justify-center gap-2 transition-all"
          >
            <Search className="w-4 h-4" /> Cerca altri tutorial su YouTube
          </a>
        </div>
      </div>
    </div>
  );
}
